import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { User } from '@app/_models';
import { Certificate } from '@app/_models/certificate';
import { Dealer } from '@app/_models/dealer';
import { AccountService, AlertService } from '@app/_services';
import { DealerService } from '@app/_services/dealer-service';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { AgGridAngular } from 'ag-grid-angular';
import { GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { ReportSearch } from '../../_models/reportSearch';
import { SearchService } from '../../_services/search.service';


@Component({
  selector: 'app-certificate-report',
  templateUrl: './certificate-report.component.html',
  styleUrls: ['./certificate-report.component.less']
})
export class CertificateReportComponent implements OnInit, OnDestroy {
  @ViewChild('aggrid') aggrid: AgGridAngular;
  public gridApi: GridApi;
  certificateReportForm: UntypedFormGroup;
  id: string;
  submitted = false;
  issueDateFrom: NgbDateStruct;
  issueDateTo: NgbDateStruct;
  gridOptions: GridOptions;
  loading = false;
  gridReady = true;
  reportSearch: ReportSearch;
  issueDateFromSearch: string;
  issueDateToSearch: string;
  // TODO: Replace `any[]` with a proper Certificate/Report interface when the API response shape is defined
  reportCertificateData: any[];
  user: User;
  dealer: Dealer;

  private destroy$ = new Subject<void>();
  private resizeHandler: (() => void) | null = null;

  constructor(
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private searchService: SearchService,
    private alertService: AlertService,
    private accountService: AccountService,
    private dealerService: DealerService
  ) { }

  ngOnInit() {
    this.user = this.accountService.userValue;
    this.id = this.route.snapshot.params['id'];
    this.initializeGrid();
    this.initializeForm();
    this.setupColumnDefinitions();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up window resize listener
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
  }

  private initializeGrid() {
    this.gridOptions = <GridOptions>{
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 120
      },
      suppressRowClickSelection: true,
      rowSelection: 'multiple',
      pagination: true,
      paginationPageSize: 50,
      suppressMenuHide: true,
      rowData: [], // Initialize with empty array
      animateRows: true,
      enableCellTextSelection: true,
      suppressHorizontalScroll: false
    };
  }

  private initializeForm() {
    this.certificateReportForm = this.formBuilder.group({
      packageNumber: [''],
      policyNumber: [''],
      firstName: [''],
      lastName: [''],
      userName: [''],
      issueDateFrom: [''],
      issueDateTo: [''],
      dealerId: [''],
      dealerGroupName: [''],
    });
  }

  private setupColumnDefinitions() {

    if (this.id == "NewIndiaPolicy") {
      // NewIndiaPolicy column definitions are currently disabled
    } else {

      if ((this.user.role == "executive") || (this.user.role == "manager"))
        this.gridOptions.columnDefs = [
          { headerName: "Package Number", field: "packageNumber", sortable: true, filter: true }
          , { headerName: "Policy Number", field: "insurancePolicyNumber", sortable: true, filter: true }
          , { headerName: "Certificate Id", field: "certificateNumber", sortable: true, filter: true }
          , { headerName: "First Name", field: "firstName", sortable: true, filter: true }
          , { headerName: "Last Name", field: "lastName", sortable: true, filter: true }
          , { headerName: "Registration Number", field: "registrationNumber", sortable: true, filter: true }
          , { headerName: "Chasis Number", field: "chasisNumber", sortable: true, filter: true }
          , { headerName: "Engine Number", field: "engineNumber", sortable: true, filter: true }
          , { headerName: "Model", field: "model", sortable: true, filter: true }
          , { headerName: "Variant", field: "variant", sortable: true, filter: true }
          , { headerName: "Plan", field: "planId", sortable: true, filter: true }
          , { headerName: "Total Amount", field: "packagePolicyTotalAmount", sortable: true, filter: true }
          , { headerName: "Customer City", field: "city", sortable: true, filter: true }
          , { headerName: "Customer State", field: "state", sortable: true, filter: true }
          , { headerName: "Contact Number", field: "contactNumber", sortable: true, filter: true }
          , { headerName: "Nominee Name", field: "nomineeName", sortable: true, filter: true }
          , { headerName: "Nominee Relationship", field: "nomineeRelationship", sortable: true, filter: true }
          , { headerName: "Executive Username", field: "userName", sortable: true, filter: true }
          , { headerName: "Break Inspection Report", field: "breakInspectionReportUrl", sortable: true, filter: true }
          , { headerName: "Break Inspection Date", field: "breakInspectionReportDate", sortable: true, filter: true }
          , { headerName: "Break Inspection Time", field: "breakInspectionReportTime", sortable: true, filter: true }
          , { headerName: "Break Inspection Number", field: "breakInspectionReportNumber", sortable: true, filter: true }
          , { headerName: "Certificate Status", field: "certificateStatus", sortable: true, filter: true }
          , { headerName: "Issue Date", field: "issueDateString", sortable: true, filter: true }
          , { headerName: "Created Date and Time", field: "createdDateString", sortable: true, filter: true }
        ]
      else {
        if (this.user.role == "dealerOwner") {
          this.gridOptions.columnDefs = [
            { headerName: "Package Number", field: "packageNumber", sortable: true, filter: true }
            , { headerName: "Policy Number", field: "insurancePolicyNumber", sortable: true, filter: true }
            , { headerName: "Certificate Id", field: "certificateNumber", sortable: true, filter: true }
            , { headerName: "Executive Name", field: "userName", sortable: true, filter: true }
            , { headerName: "Certificate Status", field: "certificateStatus", sortable: true, filter: true }
            , { headerName: "First Name", field: "firstName", sortable: true, filter: true }
            , { headerName: "Last Name", field: "lastName", sortable: true, filter: true }
            , { headerName: "Contact Number", field: "contactNumber", sortable: true, filter: true }
            , { headerName: "Customer DOB", field: "dob", sortable: true, filter: true }
            , { headerName: "Customer Age", field: "age", sortable: true, filter: true }
            , { headerName: "Customer City", field: "city", sortable: true, filter: true }
            , { headerName: "Nominee Name", field: "nomineeName", sortable: true, filter: true }
            , { headerName: "Nominee Relationship", field: "nomineeRelationship", sortable: true, filter: true }
            , { headerName: "Nominee Age", field: "nomineeAge", sortable: true, filter: true }
            , { headerName: "VehicleMake", field: "vehicleMake", sortable: true, filter: true }
            , { headerName: "Model", field: "model", sortable: true, filter: true }
            , { headerName: "Variant", field: "variant", sortable: true, filter: true }
            , { headerName: "Vehicle Year", field: "vehicleYear", sortable: true, filter: true }
            , { headerName: "Registration Number", field: "registrationNumber", sortable: true, filter: true }
            , { headerName: "Chasis Number", field: "chasisNumber", sortable: true, filter: true }
            , { headerName: "Engine Number", field: "engineNumber", sortable: true, filter: true }
            , { headerName: "Plan", field: "planId", sortable: true, filter: true }
            , { headerName: "Dealer Plan Type", field: "dealerPlanType", sortable: true, filter: true }
            , { headerName: "Total Paid Amount", field: "packagePolicyTotalAmount", sortable: true, filter: true }
            , { headerName: "Customer State", field: "state", sortable: true, filter: true }
            , { headerName: "Break Inspection Report", field: "breakInspectionReportUrl", sortable: true, filter: true }
            , { headerName: "Break Inspection Date", field: "breakInspectionReportDate", sortable: true, filter: true }
            , { headerName: "Break Inspection Time", field: "breakInspectionReportTime", sortable: true, filter: true }
            , { headerName: "Break Inspection Number", field: "breakInspectionReportNumber", sortable: true, filter: true }
            , { headerName: "Issue Date", field: "issueDateString", sortable: true, filter: true }
            , { headerName: "Dealer Id", field: "dealerId", sortable: true, filter: true }
            , { headerName: "Dealer Name", field: "dealerName", sortable: true, filter: true }
            , { headerName: "Dealer Balance", field: "dealerBalance", sortable: true, filter: true }
            , { headerName: "Created Timestamp", field: "createdDateString", sortable: true, filter: true }
            , { headerName: "Email", field: "email", sortable: true, filter: true }
          ];
        }
        else {
          if (this.user.role == "employee")
            this.gridOptions.columnDefs = [
              { headerName: "Package Number", field: "packageNumber", sortable: true, filter: true }
              , { headerName: "Policy Number", field: "insurancePolicyNumber", sortable: true, filter: true }
              , { headerName: "Certificate Id", field: "certificateNumber", sortable: true, filter: true }
              , { headerName: "Dealer Id", field: "dealerId", sortable: true, filter: true }
              , { headerName: "Dealer Name", field: "dealerName", sortable: true, filter: true }
              , { headerName: "Dealer Balance", field: "dealerBalance", sortable: true, filter: true }
              , { headerName: "First Name", field: "firstName", sortable: true, filter: true }
              , { headerName: "Last Name", field: "lastName", sortable: true, filter: true }
              , { headerName: "Registration Number", field: "registrationNumber", sortable: true, filter: true }
              , { headerName: "Chasis Number", field: "chasisNumber", sortable: true, filter: true }
              , { headerName: "Engine Number", field: "engineNumber", sortable: true, filter: true }
              , { headerName: "Model", field: "model", sortable: true, filter: true }
              , { headerName: "Variant", field: "variant", sortable: true, filter: true }
              , { headerName: "Plan", field: "planId", sortable: true, filter: true }
              , { headerName: "Total Amount", field: "packagePolicyTotalAmount", sortable: true, filter: true }
              , { headerName: "Customer City", field: "city", sortable: true, filter: true }
              , { headerName: "Customer State", field: "state", sortable: true, filter: true }
              , { headerName: "Contact Number", field: "contactNumber", sortable: true, filter: true }
              , { headerName: "Nominee Name", field: "nomineeName", sortable: true, filter: true }
              , { headerName: "Nominee Relationship", field: "nomineeRelationship", sortable: true, filter: true }
              , { headerName: "Executive Username", field: "userName", sortable: true, filter: true }
              , { headerName: "Break Inspection Report", field: "breakInspectionReportUrl", sortable: true, filter: true }
              , { headerName: "Break Inspection Date", field: "breakInspectionReportDate", sortable: true, filter: true }
              , { headerName: "Break Inspection Time", field: "breakInspectionReportTime", sortable: true, filter: true }
              , { headerName: "Break Inspection Number", field: "breakInspectionReportNumber", sortable: true, filter: true }
              , { headerName: "Certificate Status", field: "certificateStatus", sortable: true, filter: true }
              , { headerName: "Issue Date", field: "issueDateString", sortable: true, filter: true }
              , { headerName: "Created Date and Time", field: "createdDateString", sortable: true, filter: true }
            ]
          else
            if (this.user.specialRoleAccess.includes("ViewNetAmountGST")) {
              this.gridOptions.columnDefs = [
                { headerName: "Certificate Id", field: "certificateNumber", sortable: true, filter: true }
                , { headerName: "Dealer Id", field: "dealerId", sortable: true, filter: true }
                , { headerName: "Dealer Name", field: "dealerName", sortable: true, filter: true }
                , { headerName: "Dealer Balance", field: "dealerBalance", sortable: true, filter: true }
                , { headerName: "First Name", field: "firstName", sortable: true, filter: true }
                , { headerName: "Last Name", field: "lastName", sortable: true, filter: true }
                , { headerName: "Contact Number", field: "contactNumber", sortable: true, filter: true }
                , { headerName: "Customer DOB", field: "dob", sortable: true, filter: true }
                , { headerName: "Customer Age", field: "age", sortable: true, filter: true }
                , { headerName: "Customer City", field: "city", sortable: true, filter: true }
                , { headerName: "Nominee Name", field: "nomineeName", sortable: true, filter: true }
                , { headerName: "Nominee Relationship", field: "nomineeRelationship", sortable: true, filter: true }
                , { headerName: "Nominee Age", field: "nomineeAge", sortable: true, filter: true }
                , { headerName: "VehicleMake", field: "vehicleMake", sortable: true, filter: true }
                , { headerName: "Model", field: "model", sortable: true, filter: true }
                , { headerName: "Variant", field: "variant", sortable: true, filter: true }
                , { headerName: "Vehicle Year", field: "vehicleYear", sortable: true, filter: true }
                , { headerName: "Registration Number", field: "registrationNumber", sortable: true, filter: true }
                , { headerName: "Chasis Number", field: "chasisNumber", sortable: true, filter: true }
                , { headerName: "Engine Number", field: "engineNumber", sortable: true, filter: true }
                , { headerName: "Plan", field: "planId", sortable: true, filter: true }
                , { headerName: "Policy Type", field: "policyType", sortable: true, filter: true }
                , { headerName: "Dealer Plan Type", field: "dealerPlanType", sortable: true, filter: true }
                , { headerName: "Total Paid Amount", field: "packagePolicyTotalAmount", sortable: true, filter: true }
                , { headerName: "Customer State", field: "state", sortable: true, filter: true }
                , { headerName: "Break Inspection Report", field: "breakInspectionReportUrl", sortable: true, filter: true }
                , { headerName: "Break Inspection Date", field: "breakInspectionReportDate", sortable: true, filter: true }
                , { headerName: "Break Inspection Time", field: "breakInspectionReportTime", sortable: true, filter: true }
                , { headerName: "Break Inspection Number", field: "breakInspectionReportNumber", sortable: true, filter: true }
                , { headerName: "Issue Date", field: "issueDateString", sortable: true, filter: true }
                , { headerName: "Executive Name", field: "userName", sortable: true, filter: true }
                , { headerName: "Certificate Status", field: "certificateStatus", sortable: true, filter: true }
                , { headerName: "Created Date", field: "createdDateString", sortable: true, filter: true }
                , { headerName: "Email", field: "email", sortable: true, filter: true }
              ];

            }
            else {
              this.gridOptions.columnDefs = [
                { headerName: "Package Number", field: "packageNumber", sortable: true, filter: true }
                , { headerName: "Policy Number", field: "insurancePolicyNumber", sortable: true, filter: true }
                , { headerName: "Certificate Id", field: "certificateNumber", sortable: true, filter: true }
                , { headerName: "Dealer Id", field: "dealerId", sortable: true, filter: true }
                , { headerName: "Dealer Name", field: "dealerName", sortable: true, filter: true }
                , { headerName: "Dealer Balance", field: "dealerBalance", sortable: true, filter: true }
                , { headerName: "First Name", field: "firstName", sortable: true, filter: true }
                , { headerName: "Last Name", field: "lastName", sortable: true, filter: true }
                , {
                  headerName: "Aadhar Number", valueGetter: (params) => {
                    if (!params.data.aadhaarNumber || params.data.aadhaarNumber === null || params.data.aadhaarNumber === undefined) {
                      return 'N/A';
                    }
                    const aadhaarStr = params.data.aadhaarNumber.toString();
                    if (aadhaarStr.length < 12) {
                      return aadhaarStr;
                    }
                    return aadhaarStr.substring(0, 4) + " " + aadhaarStr.substring(4, 8) + " " + aadhaarStr.substring(8, 12);
                  }, sortable: true, filter: true
                }
                , { headerName: "Pan Number", field: "panNumber", sortable: true, filter: true }
                , { headerName: "Contact Number", field: "contactNumber", sortable: true, filter: true }
                , { headerName: "Address", field: "address", sortable: true, filter: true }
                , { headerName: "Customer DOB", field: "dob", sortable: true, filter: true }
                , { headerName: "Customer Age", field: "age", sortable: true, filter: true }
                , { headerName: "Customer City", field: "city", sortable: true, filter: true }
                , { headerName: "Nominee Name", field: "nomineeName", sortable: true, filter: true }
                , { headerName: "Nominee Relationship", field: "nomineeRelationship", sortable: true, filter: true }
                , { headerName: "Nominee Age", field: "nomineeAge", sortable: true, filter: true }
                , { headerName: "RTO", field: "rto", sortable: true, filter: true }
                , { headerName: "VehicleMake", field: "vehicleMake", sortable: true, filter: true }
                , { headerName: "Model", field: "model", sortable: true, filter: true }
                , { headerName: "Variant", field: "variant", sortable: true, filter: true }
                , { headerName: "Vehicle Year", field: "vehicleYear", sortable: true, filter: true }
                , { headerName: "Registration Number", field: "registrationNumber", sortable: true, filter: true }
                , { headerName: "Chasis Number", field: "chasisNumber", sortable: true, filter: true }
                , { headerName: "Engine Number", field: "engineNumber", sortable: true, filter: true }
                , { headerName: "Package Name", field: "package.packageName", sortable: true, filter: true }
                , { headerName: "Insurance Company", field: "insuranceCompany", sortable: true, filter: true }
                , { headerName: "Plan", field: "planId", sortable: true, filter: true }
                , { headerName: "Plan Type", field: "policyType", sortable: true, filter: true }
                , { headerName: "Dealer Plan Type", field: "dealerPlanType", sortable: true, filter: true }
                , { headerName: "Invoice Amount & Accessories", field: "idv", sortable: true, filter: true }
                , { headerName: "Total Package Amount", field: "packagePolicyTotalAmount", sortable: true, filter: true }
                , { headerName: "Package Amount", field: "packagePolicyAmount", sortable: true, filter: true }
                , { headerName: "Package GST Amount", field: "packagePolicyGStAmount", sortable: true, filter: true }
                , { headerName: "Total Policy Amount", field: "insurancePolicyTotalAmount", sortable: true, filter: true }
                , { headerName: "Policy Amount", field: "insurancePolicyAmount", sortable: true, filter: true }
                , { headerName: "Policy GST Amount", field: "insurancePolicyGStAmount", sortable: true, filter: true }
                , { headerName: "Total Certificate Amount", field: "planTotalAmount", sortable: true, filter: true }
                , { headerName: "Plan Amount", field: "planAmount", sortable: true, filter: true }
                , { headerName: "plan GST Amount", field: "planGSTAmount", sortable: true, filter: true }
                , { headerName: "Product Cost", field: "NetPlanAmount", sortable: true, filter: true }
                , { headerName: "Product Net GST amount", field: "NetPlanGSTAmount", sortable: true, filter: true }
                , { headerName: "Product Total Amount", field: "NetPlanTotalAmount", sortable: true, filter: true }
                , { headerName: "State GST", field: "SgstAmount", sortable: true, filter: true }
                , { headerName: "Center GST", field: "CgstAmount", sortable: true, filter: true }
                , { headerName: "Integrated GST", field: "IgstAmount", sortable: true, filter: true }
                , { headerName: "Customer State", field: "state", sortable: true, filter: true }
                , { headerName: "Break Inspection Report", field: "breakInspectionReportUrl", sortable: true, filter: true }
                , { headerName: "Break Inspection Date", field: "breakInspectionReportDate", sortable: true, filter: true }
                , { headerName: "Break Inspection Time", field: "breakInspectionReportTime", sortable: true, filter: true }
                , { headerName: "Break Inspection Number", field: "breakInspectionReportNumber", sortable: true, filter: true }
                , { headerName: "Issue Date", field: "issueDateString", sortable: true, filter: true }
                , { headerName: "Hypothecated", field: "hypothecated", sortable: true, filter: true }
                , { headerName: "Fianance Company Name", field: "financeCompanyName", sortable: true, filter: true }
                , { headerName: "Finance Company Address", field: "financeCompanyAddress", sortable: true, filter: true }
                , { headerName: "Issue Date", field: "issueDateString", sortable: true, filter: true }
                , { headerName: "Executive Name", field: "userName", sortable: true, filter: true }
                , { headerName: "Certificate Status", field: "certificateStatus", sortable: true, filter: true }
                , { headerName: "Created Date", field: "createdDateString", sortable: true, filter: true }
                , { headerName: "Email", field: "email", sortable: true, filter: true }
                , { headerName: "prePolClaim", field: "prePolClaim", sortable: true, filter: true }
                , { headerName: "transferCase", field: "transferCase", sortable: true, filter: true }
                , { headerName: "Previous Policy Expiry", field: "prevPolicyExpire", sortable: true, filter: true }
                , { headerName: "nilDep", field: "nilDep", sortable: true, filter: true }
                , { headerName: "Previous Od Insurer", field: "prevOdInsurer", sortable: true, filter: true }
                , { headerName: "Previous Od PolicyNo", field: "prevOdPolicyNo", sortable: true, filter: true }
                , { headerName: "Previous Od PolicyExpiry", field: "prevOdPolicyExpiry.year + '-' + prevOdPolicyExpiry.month + '-' + prevOdPolicyExpiry.day", sortable: true, filter: true }
                , { headerName: "Previous Tp Insurer", field: "prevTpInsurer", sortable: true, filter: true }
                , { headerName: "Previous Tp PolicyNo", field: "prevTpPolicyNo", sortable: true, filter: true }
                , { headerName: "Previous Tp PolicyExpiry", field: "prevTpPolicyExpiry.year + '-' + prevTpPolicyExpiry.month + '-' + prevTpPolicyExpiry.day", sortable: true, filter: true }
                , { headerName: "Pincode", field: "pincode", sortable: true, filter: true }
                , { headerName: "Processed By", field: "userNameProcessed", sortable: true, filter: true }
              ];
            }
        }
    }
    if (this.user.specialRoleAccess && this.user.specialRoleAccess.includes('CanSeeSplitAmountInPackageReport')) {
      this.gridOptions.columnDefs.push({ headerName: "Insurance Amount", field: "insurancePolicyTotalAmount", sortable: true, filter: true });
      this.gridOptions.columnDefs.push({ headerName: "AAPL Amount", field: "planTotalAmount", sortable: true, filter: true });
      this.gridOptions.columnDefs.filter(x => x["field"] != "packagePolicyTotalAmount");
      this.gridOptions.columnDefs.push({ headerName: "Total Amount", field: "packagePolicyTotalAmount", sortable: true, filter: true });
    }
  }

    // Ensure rowData is initialized
    if (!this.gridOptions.rowData) {
      this.gridOptions.rowData = [];
    }

    // Force grid refresh if already initialized
    if (this.gridApi) {
      this.gridApi.setColumnDefs(this.gridOptions.columnDefs);
      this.gridApi.sizeColumnsToFit();
    }

    // Note: gridApi won't be available until onGridReady is called
    // Dealer balance visibility will be handled in onGridReady
    this.gridReady = true;

    this.certificateReportForm.get('issueDateFrom').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value) => {
          if (value != undefined) {
            this.issueDateFromSearch = value.year + '-' + ('0' + value.month).slice(-2) + '-' + ('0' + value.day).slice(-2);
          }
        },
        error: (err) => {
          this.alertService.error('Error processing issue date from');
        }
      });

    this.certificateReportForm.get('issueDateTo').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value) => {
          if (value != undefined) {
            this.issueDateToSearch = value.year + '-' + ('0' + value.month).slice(-2) + '-' + ('0' + value.day).slice(-2);
          }
        },
        error: (err) => {
          this.alertService.error('Error processing issue date to');
        }
      });

  }
  // convenience getter for easy access to form fields
  get f() { return this.certificateReportForm.controls; }

  dealerOwnerGridColumn() {
    this.dealerService.getByDealerId(this.user.dealerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (y) => {
          this.dealer = y;
          if (this.dealer.dealerInvoiceShow && this.dealer.dealerInvoiceShow == "No") {
            this.aggrid.api.setColumnDefs(
              [
                { headerName: "Certificate Id", field: "certificateNumber", sortable: true, filter: true }
                , { headerName: "Executive Name", field: "userName", sortable: true, filter: true }
                , { headerName: "Certificate Status", field: "certificateStatus", sortable: true, filter: true }
                , { headerName: "First Name", field: "firstName", sortable: true, filter: true }
                , { headerName: "Last Name", field: "lastName", sortable: true, filter: true }
                , { headerName: "Contact Number", field: "contactNumber", sortable: true, filter: true }
                , { headerName: "Customer DOB", field: "dob", sortable: true, filter: true }
                , { headerName: "Customer Age", field: "age", sortable: true, filter: true }
                , { headerName: "Customer City", field: "city", sortable: true, filter: true }
                , { headerName: "Nominee Name", field: "nomineeName", sortable: true, filter: true }
                , { headerName: "Nominee Relationship", field: "nomineeRelationship", sortable: true, filter: true }
                , { headerName: "Nominee Age", field: "nomineeAge", sortable: true, filter: true }
                , { headerName: "VehicleMake", field: "vehicleMake", sortable: true, filter: true }
                , { headerName: "Model", field: "model", sortable: true, filter: true }
                , { headerName: "Variant", field: "variant", sortable: true, filter: true }
                , { headerName: "Vehicle Year", field: "vehicleYear", sortable: true, filter: true }
                , { headerName: "Registration Number", field: "registrationNumber", sortable: true, filter: true }
                , { headerName: "Chasis Number", field: "chasisNumber", sortable: true, filter: true }
                , { headerName: "Engine Number", field: "engineNumber", sortable: true, filter: true }
                , { headerName: "Plan", field: "planId", sortable: true, filter: true }
                , { headerName: "Dealer Plan Type", field: "dealerPlanType", sortable: true, filter: true }
                , { headerName: "Plan Amount", field: "planAmount", sortable: true, filter: true }
                , {
                  headerName: "Eligible Amount", valueGetter: (params) => {
                    if (!params.data.planAmount || !params.data.NetPlanAmount ||
                      params.data.planAmount === null || params.data.NetPlanAmount === null) {
                      return '0.00';
                    }
                    return (params.data.planAmount - params.data.NetPlanAmount).toFixed(2);
                  }, sortable: true, filter: true
                }
                , { headerName: "Customer State", field: "state", sortable: true, filter: true }
                , { headerName: "Issue Date", field: "issueDateString", sortable: true, filter: true }
                , { headerName: "Dealer Id", field: "dealerId", sortable: true, filter: true }
                , { headerName: "Dealer Name", field: "dealerName", sortable: true, filter: true }
                , { headerName: "Dealer Balance", field: "dealerBalance", sortable: true, filter: true }
                , { headerName: "Created Timestamp", field: "createdDateString", sortable: true, filter: true }
                , { headerName: "Email", field: "email", sortable: true, filter: true }
              ]
            )
          }
        },
        error: (err) => {
          this.alertService.error('Failed to load dealer information');
        }
      })
  }

  // Enhanced search functionality
  onSearch() {
    this.loading = true;
    const formValue = this.certificateReportForm.value;

    // Enhanced validation with user feedback
    if (!this.validateSearchForm(formValue)) {
      this.loading = false;
      return;
    }

    const searchParams = this.buildSearchParams(formValue);

    this.searchService.getPackageReport(searchParams as unknown as ReportSearch)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.reportCertificateData = data;
          this.updateGridData();
          this.loading = false;
          this.showSuccessMessage('Report loaded successfully');
        },
        error: (error) => {
          this.loading = false;
          this.showErrorMessage('Failed to load report. Please try again.');
        }
      });
  }

  private validateSearchForm(formValue: Record<string, unknown>): boolean {
    if (this.user.role === 'DealerAdmin' || this.user.role === 'DealerUser') {
      return true; // Dealer users can search without additional criteria
    }

    // For admin users, require at least one search criterion
    const hasSearchCriteria = Object.values(formValue).some(value =>
      value !== null && value !== undefined && value !== ''
    );

    if (!hasSearchCriteria) {
      this.showErrorMessage('Please provide at least one search criterion');
      return false;
    }

    return true;
  }

  private buildSearchParams(formValue: Record<string, unknown>): Record<string, unknown> {
    const params: Record<string, unknown> = {};

    Object.keys(formValue).forEach(key => {
      if (formValue[key] !== null && formValue[key] !== undefined && formValue[key] !== '') {
        // Handle date formatting for backend
        if (key === 'issueDateFrom' || key === 'issueDateTo') {
          const dateVal = formValue[key] as { year: number; month: number; day: number };
          if (dateVal && typeof dateVal === 'object' && dateVal.year) {
            params[key] = dateVal.year + '-' +
              ('0' + dateVal.month).slice(-2) + '-' +
              ('0' + dateVal.day).slice(-2);
          }
        } else {
          params[key] = formValue[key];
        }
      }
    });

    // Set default dates if not provided
    if (!params.issueDateFrom) {
      params.issueDateFrom = '0001-01-01';
    }
    if (!params.issueDateTo) {
      params.issueDateTo = '9999-99-99';
    }

    // Add role-based filters
    if (this.user.role === 'DealerAdmin' || this.user.role === 'DealerUser') {
      params.dealerId = this.user.dealerId;
    }

    return params;
  }

  private updateGridData() {
    if (this.gridApi && this.reportCertificateData) {
      this.gridApi.setRowData(this.reportCertificateData);

      // Force grid refresh
      setTimeout(() => {
        this.gridApi.sizeColumnsToFit();
        this.gridApi.refreshCells();
      }, 100);
    }
  }

  private showSuccessMessage(message: string) {
    this.alertService.success(message);
  }

  private showErrorMessage(message: string) {
    this.alertService.error(message);
  }

  // Grid event handlers
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;

    // Set column definitions if not already set
    if (this.gridOptions.columnDefs && this.gridOptions.columnDefs.length > 0) {
      params.api.setColumnDefs(this.gridOptions.columnDefs);
    }

    // Auto-size columns to fit content
    setTimeout(() => {
      params.api.sizeColumnsToFit();
    }, 100);

    // Handle special role access for dealer balance visibility
    if (this.user.specialRoleAccess && this.user.specialRoleAccess.includes('CannotViewDealerBalance')) {
      params.api.setColumnVisible('dealerBalance', false);
    }

    // Add window resize listener (stored for cleanup in ngOnDestroy)
    this.resizeHandler = () => {
      setTimeout(() => {
        if (params.api) {
          params.api.sizeColumnsToFit();
        }
      }, 100);
    };
    window.addEventListener('resize', this.resizeHandler);
  }

  onFirstDataRendered(params: GridReadyEvent) {
    // Auto-size columns to fit content (Community version compatible)
    params.api.sizeColumnsToFit();
  }

  onSelectionChanged() {
    // Selection changed - grid rows selected/deselected
    const selectedRows = this.gridApi.getSelectedRows();
  }

  // Clear filters
  clearFilters() {
    if (this.gridApi) {
      this.gridApi.setFilterModel(null);
    }
  }

  // Reset form
  resetForm() {
    this.certificateReportForm.reset();
    this.reportCertificateData = [];
    if (this.gridApi) {
      this.gridApi.setRowData([]);
    }
  }

  onSubmit() {
    this.submitted = true;
    this.loading = true;
    this.alertService.clear();

    if (this.user.id == undefined) {
      this.alertService.error("User Credential Missing, Please login again and try");
      return
    }

    this.reportSearch = new ReportSearch();
    this.reportSearch.userId = this.user.id;

    // Handle date formatting with proper defaults
    if (this.certificateReportForm.get('issueDateFrom').value) {
      const fromDate = this.certificateReportForm.get('issueDateFrom').value;
      this.reportSearch.issueDateFrom = fromDate.year + '-' +
        ('0' + fromDate.month).slice(-2) + '-' +
        ('0' + fromDate.day).slice(-2);
    } else {
      this.reportSearch.issueDateFrom = "0001-01-01";
    }

    if (this.certificateReportForm.get('issueDateTo').value) {
      const toDate = this.certificateReportForm.get('issueDateTo').value;
      this.reportSearch.issueDateTo = toDate.year + '-' +
        ('0' + toDate.month).slice(-2) + '-' +
        ('0' + toDate.day).slice(-2);
    } else {
      this.reportSearch.issueDateTo = "9999-99-99";
    }


    this.reportSearch.certificateNumber = this.certificateReportForm.get('packageNumber').value;
    this.reportSearch.firstName = this.certificateReportForm.get('firstName').value;
    this.reportSearch.lastName = this.certificateReportForm.get('lastName').value;
    this.reportSearch.userName = this.certificateReportForm.get('userName').value;
    this.reportSearch.dealerId = this.certificateReportForm.get('dealerId').value;
    this.reportSearch.dealerGroupName = this.certificateReportForm.get('dealerGroupName').value;
    this.reportSearch.policyNumber = this.certificateReportForm.get('policyNumber').value;


    if ((this.user.role == "admin") || (this.user.role == "dealerOwner")) {
      this.searchService.getPackageReport(this.reportSearch)
        .pipe(first(), takeUntil(this.destroy$))
        .subscribe({
          next: (x) => {
            this.reportCertificateData = x.map(a => {
              a.createdDateString = new Date(a.createdDate).toString();
              if (this.id == "NewIndiaPolicy") {
                a.endDate = new Date(a.endDate).getFullYear() + "-" + ('0' + (new Date(a.endDate).getMonth() + 1)).slice(-2)
                  + "-" + ('0' + (new Date(a.endDate).getDate())).slice(-2) + " MIDNIGHT";
              }
              return a;
            });

            if (this.id == "NewIndiaPolicy") {
              this.reportCertificateData = this.reportCertificateData.filter(x => x.policyNumber != "NA");
            }

            this.aggrid.api.setRowData(this.reportCertificateData);

            if (this.user.specialRoleAccess && this.user.specialRoleAccess.includes('CannotViewDealerBalance')) {
              this.gridApi.setColumnVisible('dealerBalance', false);
            }

            // Force grid refresh
            setTimeout(() => {
              if (this.gridApi) {
                this.gridApi.sizeColumnsToFit();
                this.gridApi.refreshCells();
              }
            }, 100);
            this.gridReady = true;
            this.loading = false;
          },
          error: error => {
            this.alertService.error(error)
            this.loading = false;
          }
        })
    }
    else {
      this.searchService.getPackageReport(this.reportSearch)
        .pipe(first(), takeUntil(this.destroy$))
        .subscribe({
          next: (x) => {
            this.reportCertificateData = x.map(a => {
              a.createdDateString = new Date(a.createdDate).toString();
              return a;
            });
            this.aggrid.api.setRowData(this.reportCertificateData);

            if (this.user.specialRoleAccess && this.user.specialRoleAccess.includes('CannotViewDealerBalance')) {
              this.gridApi.setColumnVisible('dealerBalance', false);
            }

            // Force grid refresh
            setTimeout(() => {
              if (this.gridApi) {
                this.gridApi.sizeColumnsToFit();
                this.gridApi.refreshCells();
              }
            }, 100);
            this.gridReady = true;
            this.loading = false;
          },
          error: error => {
            this.alertService.error(error)
            this.loading = false;
          }
        });
    }
    return;
  }
  onReset() {

  }
  exportToExcel() {
    const params = {
      allColumns: true,
      fileName: 'certificate-report.csv'
    };
    this.aggrid.api.exportDataAsCsv(params);
    // Note: Excel export (exportDataAsExcel) requires ag-Grid Enterprise license
  }
}
